import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { selectSelectedTakeActors } from "../../../app/slices/TrainingSlice";
import PiChipSelect, { PiChipSelectOption } from "../../../components/PiChipSelect";
import { PiChipProps } from "../../../components/PiChip";
import ActorAvatar from "../../../components/actors/ActorAvatar";
import EmptyAvatar from "../../../components/actors/EmptyAvatar";
import usePropHelper from "../../../hooks/PropHelper";
import { keepCurrentLookatOption, noLookatOption } from "./lookatOptions";

export interface PiActorChipSelectProps {
  value: string;
  onChange: (actorId: string) => void;
  placeholder?: string;
  "aria-label"?: string;
  /** Actor ids left out of the list. The current value is always kept. */
  excludeActors?: string[];
  /** Leave out actors that are attached to a prop. */
  hideActorsWithProp?: boolean;
  /** Adds a "No one" option (value NO_LOOKAT) near the top: the actor stops looking. */
  allowNoActor?: boolean;
  /** Adds a "Keep current" option (value "") at the top: whatever the actor was looking at carries on. */
  allowKeepCurrent?: boolean;
  disabled?: boolean;
  /** "entity" (default) for who speaks in an interaction; "setting" to match a column of setting chips. */
  tone?: PiChipProps["tone"];
}

/** Actor dropdown for interaction previews, in the PiChipSelect style. */
export default function PiActorChipSelect(props: PiActorChipSelectProps) {
  const { value, onChange, excludeActors, hideActorsWithProp, allowNoActor, allowKeepCurrent } =
    props;
  const { t } = useTranslation();
  const actors = useSelector(selectSelectedTakeActors);
  const { getActorsNotAttachedToProps } = usePropHelper();

  const actorOptions: PiChipSelectOption[] = (
    hideActorsWithProp ? getActorsNotAttachedToProps() : actors
  )
    .filter((actor) => actor.id === value || !excludeActors?.includes(actor.id))
    .map((actor) => ({
      id: actor.id,
      label: actor.workingName,
      icon: <ActorAvatar actor={actor} size="28" />,
    }));

  const options = [
    ...(allowKeepCurrent ? [keepCurrentLookatOption(t)] : []),
    ...(allowNoActor ? [noLookatOption(t)] : []),
    ...actorOptions,
  ];

  return (
    <PiChipSelect
      value={value}
      options={options}
      disabled={props.disabled}
      tone={props.tone}
      onChange={onChange}
      placeholder={props.placeholder ?? String(t("chip_select.choose_actor"))}
      placeholderIcon={<EmptyAvatar />}
      aria-label={props["aria-label"]}
    />
  );
}
